import { SidebarMenuItem } from '../../types';

type MatchPath = (path: string, href: string) => boolean;

export const toSidebarPath = (url: string): string => {
  let path: string;

  try {
    path = new URL(url).pathname;
  } catch {
    [path] = url.split(/[?#]/);
  }

  return path === '/' ? path : path.replace(/\/+$/, '');
};

const toSidebarParams = (url: string): [string, string][] => {
  const [, query] = url.split('#')[0].split('?');

  if (!query) {
    return [];
  }

  return [...new URLSearchParams(query).entries()];
};

const isSameHost = (url: string): boolean => {
  try {
    const { host } = new URL(url, window.location.href);

    return host === window.location.host;
  } catch {
    return true;
  }
};

export const matchesPath = (path: string, href: string): boolean => {
  if (!href) {
    return false;
  }

  if (href === '/') {
    return path === href;
  }

  if (href === '/settings') {
    return (
      path === href ||
      path.includes('/event-types') ||
      path.includes('/settings')
    );
  }

  return (
    path === href ||
    path === `/employee${href}` ||
    path.startsWith(`${href}/`) ||
    path.startsWith(`/employee${href}/`)
  );
};

const matchesParams = (
  current: [string, string][],
  target: [string, string][],
): boolean => {
  return target.every(([name, value]) => {
    return current.some(([currentName, currentValue]) => {
      return currentName === name && currentValue === value;
    });
  });
};

const matchesLink = (
  currentUrl: string,
  link: string,
  matchPath: MatchPath,
): boolean => {
  if (!isSameHost(link)) {
    return false;
  }

  if (!matchPath(toSidebarPath(currentUrl), toSidebarPath(link))) {
    return false;
  }

  return matchesParams(toSidebarParams(currentUrl), toSidebarParams(link));
};

const isMoreSpecific = (candidate: string, link: string): boolean => {
  const candidatePath = toSidebarPath(candidate);
  const linkPath = toSidebarPath(link);

  if (candidatePath.length !== linkPath.length) {
    return candidatePath.length > linkPath.length;
  }

  return toSidebarParams(candidate).length > toSidebarParams(link).length;
};

export const collectSidebarLinks = (items: SidebarMenuItem[]): string[] => {
  return items.flatMap((item) => {
    return [
      ...('href' in item && toSidebarPath(item.href) ? [item.href] : []),
      ...('children' in item ? collectSidebarLinks(item.children) : []),
    ];
  });
};

export const isSidebarLinkActive = (
  currentUrl: string,
  link: string,
  menuLinks: string[] = [],
  matchPath: MatchPath = matchesPath,
): boolean => {
  if (!matchesLink(currentUrl, link, matchPath)) {
    return false;
  }

  return !menuLinks.some((candidate) => {
    return (
      isMoreSpecific(candidate, link) &&
      matchesLink(currentUrl, candidate, matchPath)
    );
  });
};
